/**
 * Permission Engine — Evaluates action permissions.
 *
 * Pure function. Implements the NEVER-HIDE invariant: permissions disable UI
 * elements, never remove them from the DOM.
 *
 * @module permissionEngine
 * @see FMPV-MVWC-E001-S004, FR-FMPV-055
 */

import type { MasterPatternViewPermissions } from '../types';

/**
 * Operation-to-permission mapping.
 * Maps operation strings to their corresponding permission key.
 */
const OPERATION_TO_PERMISSION: Record<string, keyof MasterPatternViewPermissions> = {
  CREATE: 'canCreate',
  READ: 'canRead',
  UPDATE: 'canUpdate',
  UPDATE_GLOBAL: 'canUpdateGlobal',
  DELETE: 'canDelete',
  CHANGE_STATUS: 'canChangeStatus',
  COMPANY_ENABLE: 'canCompanyEnable',
};

/**
 * Checks if an action is allowed based on permissions.
 *
 * When `permissions` is `undefined`, all actions default to `true` (per PRD §9).
 * When a specific permission key is `undefined`, it defaults to `true`.
 *
 * @param permissions - Permission configuration (or undefined for all-allowed)
 * @param operation - The operation to check
 * @param masterType - The master type context (for future type-specific logic)
 * @returns true if the action is allowed
 */
export function isActionAllowed(
  permissions: MasterPatternViewPermissions | undefined,
  operation: string,
  masterType: string
): boolean {
  if (permissions === undefined) {
    return true;
  }

  const permissionKey = OPERATION_TO_PERMISSION[operation];
  if (!permissionKey) {
    return true;
  }

  const value = permissions[permissionKey];
  // Default to true when the specific key is not set
  return value !== false;
}

/**
 * Returns the warning message for a disabled action (NEVER-HIDE toast).
 *
 * @param operation - The operation that is disabled
 * @returns A user-friendly message explaining the restriction
 */
export function getDisabledToastMessage(operation: string): string {
  const messages: Record<string, string> = {
    CREATE: 'No tiene permisos para crear registros.',
    READ: 'No tiene permisos para ver registros.',
    UPDATE: 'No tiene permisos para editar registros.',
    UPDATE_GLOBAL: 'No tiene permisos para editar datos globales.',
    DELETE: 'No tiene permisos para eliminar registros.',
    CHANGE_STATUS: 'No tiene permisos para cambiar el estado.',
    COMPANY_ENABLE: 'No tiene permisos para habilitar compañías.',
  };

  return messages[operation] ?? 'No tiene permisos para realizar esta acción.';
}
